<script setup lang="ts">
const _props = defineProps({
  suggestions: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  suggestionsText: {
    type: String,
    required: false,
    default: () => '',
  },
})
const emit = defineEmits(['close'])
const route = useRoute()
const router = useRouter()
// Extract query from route
const query = ref(route.query.q || '')

// Watch for query changes in the URL and update the input value
watch(
  () => route.query.q,
  (newQuery) => {
    query.value = newQuery || ''
  },
)
watch(query, () => {
  updateQuery()
})

// Update the URL when the user updates the query
const updateQuery = () => {
  router.push({
    path: route.path,
    query: { q: query.value || undefined },
  })
}

const { t } = useI18n()
const inputRef = ref<HTMLInputElement | null>(null)
onMounted(() => {
  inputRef.value?.focus()
})

const searchOpen = inject('search-open', ref(false))
const searchOpenComputed = computed(() => searchOpen.value)
watch(searchOpenComputed, async () => {
  if (searchOpen.value) {
    await nextTick() // Wait for DOM updates
    inputRef.value?.focus()
  }
})
</script>

<template>
  <div class="search-bar relative grid overscroll-contain">
    <div
      class="search-input sticky top-0 z-[2] inline-flex h-full w-full items-center justify-between bg-(--green-1) py-5 xl:py-10"
    >
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="input search font-display relative z-5 h-full w-full py-[0.2em] text-lg lg:text-3xl"
        :placeholder="t('search.title')"
      />
      <div class="buttons inline-flex items-center gap-5">
        <transition name="fade" mode="out-in">
          <button
            v-if="query"
            aria-label="clear search"
            class="relative z-5 h-full w-fit cursor-pointer transition-opacity"
            :class="{ 'pointer-events-none opacity-50': !query }"
            @click="query = ''"
          >
            <span v-if="query" class="font-display text-lg text-(--green-3) lg:text-xl">{{
              $t('search.clear')
            }}</span>
          </button>
        </transition>
        <button
          aria-label="close search"
          class="relative z-5 h-full w-fit cursor-pointer transition-opacity"
          @click="emit('close')"
        >
          <IconsCross class="size-4" />
        </button>
      </div>
    </div>
    <ElementsSearchResults
      :suggestions="suggestions"
      :suggestions-text="suggestionsText"
      @suggestion-click="query = $event"
      @close="emit('close')"
    />
  </div>
</template>

<style lang="postcss" scoped>
/**
 * transition of the search/cross icons
 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.input {
  &:focus {
    outline: none;
  }
}

.search-input:before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: var(--sand);
  opacity: 0.4;
  transition: opacity 0.2s ease-in-out;
  z-index: 1;
  @media (min-width: 1024px) {
    bottom: 0;
  }
}
.search-input:focus-within:before {
  opacity: 0.6;
}
</style>
